import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Reveal } from '@/components/motion/Reveal';
import { ArrowRight, Check } from 'lucide-react';

export const HeroSection = () => {
  return (
    <section className="relative overflow-hidden">
      {/* Background image */}
      <img
        src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=70&auto=format&fit=crop"
        srcSet="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=70&auto=format&fit=crop 800w, https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1280&q=70&auto=format&fit=crop 1280w, https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=70&auto=format&fit=crop 1600w, https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=2000&q=70&auto=format&fit=crop 2000w"
        sizes="100vw"
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-cover"
        loading="eager"
        fetchPriority="high"
      />
      {/* Layered atmospheric overlays — no orbs */}
      <div className="absolute inset-0 bg-gradient-to-br from-midnight-green/95 via-midnight-green/80 to-deep-teal/85" />
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(80% 60% at 85% 0%, hsl(var(--copper) / 0.18), transparent 60%), radial-gradient(70% 60% at 0% 100%, hsl(var(--deep-teal) / 0.4), transparent 60%)',
        }}
      />
      <div className="absolute inset-0 grain pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 lg:pt-32 lg:pb-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          {/* Headline column */}
          <div className="lg:col-span-8">
            <Reveal>
              <span className="eyebrow text-copper/90">For camp kitchens</span>
            </Reveal>

            <Reveal delay={80}>
              <h1 className="font-display text-display-xl text-ivory mt-6 text-balance">
                Run your camp kitchen{' '}
                <span className="italic text-copper" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 100' }}>
                  like a pro
                </span>
                .
              </h1>
            </Reveal>

            <Reveal delay={160}>
              <p className="mt-8 max-w-xl font-inter text-lg lg:text-xl text-ivory/75 leading-relaxed text-pretty">
                Plan menus, track inventory, and order from Sysco — all in one calm system built for camps,
                retreats, and outdoor programs.
              </p>
            </Reveal>

            <Reveal delay={240}>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-copper hover:bg-copper/90 text-ivory font-medium text-base px-7 h-12 btn-press shadow-editorial"
                >
                  <Link to="/contact" className="inline-flex items-center gap-2">
                    Request a camp demo <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Link
                  to="/#features"
                  className="story-link font-inter text-ivory/90 hover:text-ivory text-base font-medium"
                >
                  See how it works
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Side column: typographic stack */}
          <div className="lg:col-span-4 lg:pb-3">
            <Reveal delay={320}>
              <div className="flex flex-col gap-5 lg:border-l lg:border-ivory/15 lg:pl-8">
                {[
                  { k: '40+', v: 'Hours saved every month' },
                  { k: '99.7%', v: 'Sysco order accuracy' },
                  { k: '500+', v: 'Camps served nationwide' },
                ].map((stat) => (
                  <div key={stat.k} className="flex items-baseline gap-4">
                    <span className="font-display text-3xl lg:text-4xl text-copper tabular-nums">{stat.k}</span>
                    <span className="font-inter text-sm text-ivory/70">{stat.v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>

        {/* Trust strip */}
        <Reveal delay={400}>
          <div className="mt-20 pt-6 border-t border-ivory/10 flex flex-wrap items-center gap-x-8 gap-y-3 text-ivory/60 text-xs uppercase tracking-[0.18em] font-medium">
            <span className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-copper" /> Connected with Sysco
            </span>
            <span className="hidden sm:inline text-ivory/20">/</span>
            <span className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-copper" /> Trusted by camp directors
            </span>
            <span className="hidden sm:inline text-ivory/20">/</span>
            <span className="flex items-center gap-2">
              <Check className="h-3.5 w-3.5 text-copper" /> Saves 40+ hrs / month
            </span>
          </div>
        </Reveal>
      </div>

      {/* Soft fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-sand to-transparent z-10 pointer-events-none" />
    </section>
  );
};
